import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Check, Crown, Zap, Shield, Clock, ArrowRight, Loader2, Globe } from 'lucide-react';
import { Button } from '../components/ui/button';
import NavigationMenu from '../components/NavigationMenu';
import DynamicFooter from '../components/DynamicFooter';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/use-toast';
import { Toaster } from '../components/ui/toaster';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PricingPage = () => {
  const { user, token, isPro, isUltimate, isAuthenticated } = useAuth();
  const { toast } = useToast();
  const [billing, setBilling] = useState('monthly');
  const [loadingPlan, setLoadingPlan] = useState(null);
  const [cmsData, setCmsData] = useState(null);
  const [planLimits, setPlanLimits] = useState(null);

  useEffect(() => {
    axios.get(`${API}/pages/pricing`).then(r => setCmsData(r.data)).catch(() => {});
    axios.get(`${API}/public/plan-limits`).then(r => setPlanLimits(r.data)).catch(() => {});
  }, []);

  const handleSubscribe = async (plan) => {
    if (!user) {
      toast({ title: 'Connectez-vous pour souscrire', variant: 'destructive' });
      return;
    }
    setLoadingPlan(plan);
    try {
      const res = await axios.post(`${API}/payments/checkout`, {
        plan: plan,
        billing_cycle: billing === 'annual' ? (plan === 'ultimate' ? 'yearly' : 'yearly') : (plan === 'ultimate' ? 'monthly' : 'monthly'),
        origin_url: window.location.origin,
        email: user.email,
        user_id: user.user_id
      }, { headers: { Authorization: `Bearer ${token}` } });
      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      }
    } catch (err) {
      toast({ title: 'Erreur', description: err.response?.data?.detail || 'Erreur lors de la creation du paiement', variant: 'destructive' });
    }
    setLoadingPlan(null);
  };

  const proMonthly = cmsData?.pro_monthly_price || '9,99';
  const proAnnual = cmsData?.pro_annual_price || '99,99';
  const ultimateMonthly = '14,99';
  const ultimateAnnual = '149,99';

  const plans = [
    {
      name: 'Gratuit',
      price: '0€',
      period: '',
      description: 'Pour un usage occasionnel',
      features: [
        `Fichiers jusqu'a ${planLimits?.max_file_size_free || 2} Go`,
        `Liens valides ${planLimits?.expiry_days_free || 7} jours`,
        `${planLimits?.max_downloads_free || 10} telechargements max`,
        'Compression ZIP automatique',
        'QR Code de partage',
        'Chiffrement en transit',
        'Protection par mot de passe'
      ],
      cta: 'Commencer gratuitement',
      ctaLink: '/',
      popular: false,
      highlight: false,
      icon: null,
      color: 'blue'
    },
    {
      name: 'Pro',
      price: billing === 'monthly' ? `${proMonthly}€` : `${proAnnual}€`,
      period: billing === 'monthly' ? '/mois' : '/an',
      yearlyEquiv: billing === 'annual' ? `soit ~${(parseFloat(proAnnual.replace(',', '.')) / 12).toFixed(2).replace('.', ',')}€/mois` : null,
      description: 'Pour les professionnels',
      features: [
        'Tout du plan Gratuit',
        `Fichiers jusqu'a ${planLimits?.max_file_size_pro || 20} Go`,
        `${planLimits?.expiry_days_pro || 30} jours de disponibilite`,
        'Telechargements illimites',
        'Fond d\'ecran personnalise',
        'Notification de telechargement',
        'Vente de fichiers',
        'Chiffrement E2E',
        'Envoi programme'
      ],
      cta: isPro && !isUltimate ? 'Vous etes Pro' : 'Passer Pro',
      popular: true,
      highlight: true,
      icon: <Crown className="w-6 h-6 text-yellow-400" />,
      color: 'yellow',
      plan_key: 'pro'
    },
    {
      name: 'Ultimate',
      price: billing === 'monthly' ? `${ultimateMonthly}€` : `${ultimateAnnual}€`,
      period: billing === 'monthly' ? '/mois' : '/an',
      yearlyEquiv: billing === 'annual' ? `soit ~${(parseFloat(ultimateAnnual.replace(',', '.')) / 12).toFixed(2).replace('.', ',')}€/mois` : null,
      description: 'Transferts directs P2P',
      features: [
        'Tout du plan Pro',
        'Transfert Peer-to-Peer (WebRTC)',
        'Transfert direct sans serveur',
        'Aucune limite de taille en P2P',
        'Support prioritaire'
      ],
      cta: isUltimate ? 'Vous etes Ultimate' : 'Passer Ultimate',
      popular: false,
      highlight: false,
      icon: <Zap className="w-6 h-6 text-purple-400" />,
      color: 'purple',
      plan_key: 'ultimate'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900">
      <NavigationMenu />
      <Toaster />

      <main className="pt-28 pb-20 px-6">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              {cmsData?.title ? (
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-400">{cmsData.title}</span>
              ) : (
                <>Des tarifs <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-400">simples et transparents</span></>
              )}
            </h1>
            <p className="text-blue-200/70 text-lg max-w-2xl mx-auto">
              {cmsData?.subtitle || 'Choisissez le plan qui correspond a vos besoins. Passez Pro ou Ultimate pour debloquer toutes les fonctionnalites.'}
            </p>

            {/* Billing toggle */}
            <div className="flex items-center justify-center gap-3 mt-8">
              <button
                onClick={() => setBilling('monthly')}
                className={`px-6 py-2.5 rounded-xl text-sm font-medium transition-all ${billing === 'monthly' ? 'bg-blue-500 text-white shadow-lg shadow-blue-500/25' : 'text-blue-300/60 hover:text-blue-300'}`}
                data-testid="billing-monthly"
              >
                Mensuel
              </button>
              <button
                onClick={() => setBilling('annual')}
                className={`px-6 py-2.5 rounded-xl text-sm font-medium transition-all relative ${billing === 'annual' ? 'bg-blue-500 text-white shadow-lg shadow-blue-500/25' : 'text-blue-300/60 hover:text-blue-300'}`}
                data-testid="billing-annual"
              >
                Annuel
                <span className="absolute -top-2 -right-12 bg-emerald-500 text-white text-[10px] px-2 py-0.5 rounded-full font-bold">-17%</span>
              </button>
            </div>
          </div>

          {/* Pricing Cards */}
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {plans.map((plan, index) => {
              const isCurrent = (plan.name === 'Pro' && isPro && !isUltimate) || (plan.name === 'Ultimate' && isUltimate);
              const borderColor = plan.color === 'yellow' ? 'border-cyan-500/50' : plan.color === 'purple' ? 'border-purple-500/50' : 'border-blue-500/20';
              const bgClass = plan.highlight
                ? 'bg-gradient-to-br from-blue-600/20 to-cyan-600/20 border-2'
                : plan.color === 'purple'
                  ? 'bg-gradient-to-br from-purple-600/15 to-violet-600/10 border-2'
                  : 'bg-slate-800/50 border';

              return (
                <div
                  key={index}
                  className={`relative rounded-3xl p-8 flex flex-col ${bgClass} ${borderColor}`}
                  data-testid={`plan-${plan.name.toLowerCase()}`}
                >
                  {plan.popular && (
                    <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                      <span className="bg-gradient-to-r from-cyan-500 to-blue-500 text-white text-sm font-medium px-4 py-1 rounded-full">
                        Populaire
                      </span>
                    </div>
                  )}

                  {plan.name === 'Ultimate' && (
                    <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                      <span className="bg-gradient-to-r from-purple-500 to-violet-500 text-white text-sm font-medium px-4 py-1 rounded-full flex items-center gap-1">
                        <Zap className="w-3 h-3" /> Nouveau
                      </span>
                    </div>
                  )}

                  <div className="mb-6">
                    <h3 className="text-2xl font-bold text-white flex items-center gap-2">
                      {plan.name}
                      {plan.icon}
                    </h3>
                    <p className="text-blue-300/70 mt-1">{plan.description}</p>
                  </div>

                  <div className="mb-6">
                    <div className="flex items-baseline gap-1">
                      <span className="text-5xl font-bold text-white">{plan.price}</span>
                      {plan.period && <span className="text-blue-300/70">{plan.period}</span>}
                    </div>
                    {plan.yearlyEquiv && (
                      <div className="mt-2">
                        <span className="text-emerald-400 text-sm">{plan.yearlyEquiv}</span>
                      </div>
                    )}
                  </div>

                  <ul className="space-y-3 mb-8 flex-1">
                    {plan.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        <Check className={`w-5 h-5 flex-shrink-0 mt-0.5 ${plan.color === 'purple' ? 'text-purple-400' : plan.color === 'yellow' ? 'text-cyan-400' : 'text-cyan-400'}`} />
                        <span className="text-blue-100/80">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  {plan.name === 'Gratuit' ? (
                    <Link to="/">
                      <Button className="w-full py-6 font-semibold bg-slate-700 hover:bg-slate-600 text-white" data-testid="plan-free-btn">
                        {plan.cta}
                      </Button>
                    </Link>
                  ) : isCurrent ? (
                    <Button
                      className={`w-full py-6 font-semibold ${plan.color === 'purple' ? 'bg-purple-500/20 text-purple-400 border border-purple-500/30' : 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/30'}`}
                      disabled
                    >
                      {plan.icon && <span className="mr-2">{plan.icon}</span>}
                      {plan.cta}
                    </Button>
                  ) : (
                    <Button
                      onClick={() => handleSubscribe(plan.plan_key)}
                      disabled={loadingPlan === plan.plan_key}
                      className={`w-full py-6 font-semibold ${plan.color === 'purple' ? 'bg-gradient-to-r from-purple-500 to-violet-500 hover:from-purple-600 hover:to-violet-600 text-white' : 'bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white'}`}
                      data-testid={`subscribe-${plan.plan_key}-btn`}
                    >
                      {loadingPlan === plan.plan_key ? (
                        <Loader2 className="w-5 h-5 animate-spin mr-2" />
                      ) : (
                        <ArrowRight className="w-5 h-5 mr-2" />
                      )}
                      {plan.cta}
                    </Button>
                  )}
                </div>
              );
            })}
          </div>

          {/* Features Grid - Pourquoi choisir SENDBOX ? */}
          <div className="mt-20">
            <h2 className="text-2xl font-bold text-white text-center mb-10">
              Pourquoi choisir SENDBOX ?
            </h2>

            <div className="grid md:grid-cols-4 gap-6">
              <div className="bg-slate-800/30 border border-blue-500/20 rounded-xl p-6">
                <div className="w-12 h-12 bg-blue-500/20 rounded-lg flex items-center justify-center mb-4">
                  <Zap className="w-6 h-6 text-blue-400" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">Ultra rapide</h3>
                <p className="text-blue-300/70 text-sm">
                  Transfert optimise par chunks pour des uploads jusqu'a 20 Go sans interruption.
                </p>
              </div>

              <div className="bg-slate-800/30 border border-blue-500/20 rounded-xl p-6">
                <div className="w-12 h-12 bg-green-500/20 rounded-lg flex items-center justify-center mb-4">
                  <Shield className="w-6 h-6 text-green-400" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">Securise</h3>
                <p className="text-blue-300/70 text-sm">
                  Chiffrement E2E et liens temporaires pour proteger vos donnees sensibles.
                </p>
              </div>

              <div className="bg-slate-800/30 border border-blue-500/20 rounded-xl p-6">
                <div className="w-12 h-12 bg-amber-500/20 rounded-lg flex items-center justify-center mb-4">
                  <Clock className="w-6 h-6 text-amber-400" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">Expiration auto</h3>
                <p className="text-blue-300/70 text-sm">
                  Vos fichiers expirent automatiquement. Aucune trace apres la duree definie.
                </p>
              </div>

              <div className="bg-slate-800/30 border border-purple-500/20 rounded-xl p-6">
                <div className="w-12 h-12 bg-purple-500/20 rounded-lg flex items-center justify-center mb-4">
                  <Globe className="w-6 h-6 text-purple-400" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">P2P direct</h3>
                <p className="text-blue-300/70 text-sm">
                  Transfert navigateur-a-navigateur sans passer par nos serveurs. Aucune limite de taille.
                </p>
              </div>
            </div>
          </div>

          {/* Stockage France */}
          <div className="mt-12 text-center">
            <div className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-500/10 border border-blue-500/20 rounded-full">
              <Shield className="w-4 h-4 text-blue-400" />
              <span className="text-blue-200/80 text-sm">Donnees stockees en France / Europe (RGPD)</span>
            </div>
          </div>
        </div>
      </main>

      <DynamicFooter fixed={true} />
    </div>
  );
};

export default PricingPage;
